import {match, Pattern} from 'ts-pattern';

import {any, includes, isNil, nth, uniqBy} from 'ramda';

import i18n from '@dms/i18n';

import {getApiValidationErrors} from './getApiValidationErrors';

type ValidationErrors = ReturnType<typeof getApiValidationErrors>;

type MapCustomerValidationErrorsOptions = {
  /** Path of the e-mail rows in the request body, e.g. `foundingPerson.emails`. */
  emailsPath: string;
  /** Path of the e-mail rows in the form, when it differs from the request body. */
  formEmailsPath?: string;
  /** The form row of each sent e-mail, from `getEmailsRequestBody`. */
  emailFormIndexes: number[];
  /** Prefix of the form path of the other errors, when the request body is nested in the form. */
  formPathPrefix?: string;
};

/**
 * The API reports a refused value inside these nullable objects on the object itself, so the
 * error belongs to no single input.
 */
const FORM_LEVEL_ERROR_FIELDS = ['foundingPerson', 'businessInfo', 'selfEmployedBusinessInfoData'];

export const FORM_ROOT_ERROR_NAME = 'root.server';

const getFormRootError = () => ({
  name: FORM_ROOT_ERROR_NAME,
  message: i18n.t('entity.customer.validations.invalidCustomerData'),
});

const getEmailFormIndex = (name: string, options: MapCustomerValidationErrorsOptions) => {
  const prefix = `${options.emailsPath}.`;
  const [index, field, ...rest] = name.startsWith(prefix)
    ? name.slice(prefix.length).split('.')
    : [];

  return field === 'email' && rest.length === 0
    ? nth(Number(index), options.emailFormIndexes)
    : undefined;
};

/**
 * Maps the 400 `validationErrors` of a customer or contact write onto the form: an e-mail error
 * goes to the form row it came from (empty rows are not sent), and an error on a whole object
 * becomes a form-level error. Any other error goes to its field and also adds the form-level
 * error, because the field can be hidden or nested deeper than its input, and the toast is off.
 */
export const mapCustomerValidationErrors = (
  errors: ValidationErrors,
  options: MapCustomerValidationErrorsOptions
): ValidationErrors => {
  if (isNil(errors)) {
    return null;
  }

  const mappedErrors = errors.map((error) =>
    match({
      isFormLevel: includes(error.name, FORM_LEVEL_ERROR_FIELDS),
      emailFormIndex: getEmailFormIndex(error.name, options),
    })
      .with({isFormLevel: true}, getFormRootError)
      .with({emailFormIndex: Pattern.number}, ({emailFormIndex}) => ({
        name: `${options.formEmailsPath ?? options.emailsPath}.${emailFormIndex}.email`,
        message: error.message,
      }))
      .otherwise(() => ({
        name: `${options.formPathPrefix ?? ''}${error.name}`,
        message: error.message,
      }))
  );
  const hasErrorOutsideEmails = any(
    (error) => isNil(getEmailFormIndex(error.name, options)),
    errors
  );

  return uniqBy(
    (error) => error.name,
    hasErrorOutsideEmails ? [...mappedErrors, getFormRootError()] : mappedErrors
  );
};
